<template>
  <div class="mtb-page">
    <div v-if="templateData.description" class="mtb-page__description">
      {{ templateData.description }}
    </div>
    <div class="mtb-page__info">
      <div class="mtb-page__info-title">Сума до сплати</div>
      <div class="mtb-page__info-container">
        <span v-if="templateData.amount" class="mtb-page__info-amount">
          {{ formatAmount(templateData.amount, templateData.currency) }}
        </span>
        <span v-if="templateData.currency" class="mtb-page__info-currency">
          {{ templateData.currency }}
        </span>
      </div>
    </div>
    <div v-if="templateData.qr_code" class="mtb-page__qr">
      <div id="mtbQR"></div>
      <div class="mtb-page__qr-title">Відскануйте QR-код за допомогою банківського застосунку</div>
    </div>
    <div v-if="templateData.deep_link" class="mtb-page__alt">АБО</div>

    <button
      v-if="templateData.deep_link && displayBanks.length"
      class="mtb-page__deep-link"
      @click="openBankModal">
      Обрати банк
    </button>

    <div v-if="showBankModal" class="mtb-page__modal-overlay" @click="closeBankModal">
      <div class="mtb-page__modal" @click.stop>
        <div class="mtb-page__modal-header">
          <h3>Оберіть банк для оплати</h3>
          <button @click="closeBankModal" class="mtb-page__modal-close">&times;</button>
        </div>
        <div class="mtb-page__modal-content">
          <div class="mtb-page__banks-list">
            <div
              v-for="bank in displayBanks"
              :key="bank.code"
              @click="selectBank(bank)"
              class="mtb-page__bank-item">
              <img :src="bank.logo" :alt="bank.label" class="mtb-page__bank-logo" />
              <span class="mtb-page__bank-name">{{ bank.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { MoneyFormatter } from '../../../helpers/Formatting/MoneyFormatter';
import QRCodeStyling from 'qr-code-styling';
import banks from './banks.js';

export default {
  mounted() {
    if (this.templateData.qr_code) {
      this.createQrCode();
    }

    document.addEventListener('keydown', this.handleEscKey);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEscKey);
  },

  data() {
    return {
      qrCode: null,
      showBankModal: false,
      banks,
    };
  },

  computed: {
    ...mapState(['paymentInvoiceData', 'flowData']),

    isMobileSize() {
      return this.screenSize.width <= 480;
    },

    detectedDevice() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;

      if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return 'ios';
      }

      if (/android/i.test(userAgent)) {
        return 'android';
      }

      return 'desktop';
    },

    displayBanks() {
      if (this.detectedDevice === 'desktop') {
        return [];
      }

      return this.banks.filter(bank => {
        if (this.detectedDevice === 'ios' && bank.deeplink.ios) {
          return bank.deeplink.ios;
        }

        if (this.detectedDevice === 'android' && bank.deeplink.android) {
          return bank.deeplink.android;
        }

        return false;
      });
    },

    paymentInvoice() {
      return this.paymentInvoiceData ? this.paymentInvoiceData.data || this.paymentInvoiceData : {};
    },

    templateData() {
      const { description, id } = this.paymentInvoice;

      const { amount: mainAmount, currency: mainCurrency } = this.flowData;

      const {
        amount: payloadAmount,
        currency: payloadCurrency,
        requisites,
      } = this.flowData.payload;

      const parseRequisites = () => {
        if (!requisites) {
          return {};
        }

        if (typeof requisites === 'string') {
          try {
            return JSON.parse(requisites);
          } catch (e) {
            return {};
          }
        }

        return requisites;
      };

      const { deep_link, qr_code_data } = parseRequisites();

      const amount = [undefined, null].includes(payloadAmount) ? mainAmount : payloadAmount;

      const currency = [undefined, null].includes(payloadCurrency) ? mainCurrency : payloadCurrency;

      return {
        id,
        description,
        amount,
        currency,
        qr_code: deep_link || qr_code_data || '',
        deep_link,
      };
    },
  },

  methods: {
    formatAmount(amount, currency, showCurrency) {
      const formatter = new MoneyFormatter();
      return formatter.format(amount, currency, showCurrency);
    },

    createQrCode() {
      this.qrCode = new QRCodeStyling({
        width: 240,
        height: 240,
        type: 'svg',
        data: this.templateData.qr_code,
        dotsOptions: {
          color: 'black',
          type: 'dots',
        },
        cornersSquareOptions: {
          type: 'extra-rounded',
        },
        cornersDotOptions: {
          type: 'dot',
        },
        imageOptions: {
          imageSize: 0.5,
          margin: 10,
        },
      });

      this.qrCode.append(document.getElementById('mtbQR'));
    },

    openBankModal() {
      this.showBankModal = true;
    },

    closeBankModal() {
      this.showBankModal = false;
    },

    handleEscKey(event) {
      if (event.key === 'Escape' && this.showBankModal) {
        this.closeBankModal();
      }
    },

    generateDeeplink(bank) {
      const device = this.detectedDevice;
      const baseUrl = this.templateData.deep_link;

      if (device === 'ios' && bank.deeplink.ios) {
        return typeof bank.deeplink.ios === 'function'
          ? bank.deeplink.ios(baseUrl)
          : baseUrl.replace('https', bank.deeplink.ios);
      } else if (device === 'android' && bank.deeplink.android) {
        return typeof bank.deeplink.android === 'function'
          ? bank.deeplink.android(baseUrl)
          : `${baseUrl.replace('https', 'intent')}#Intent;package=${
              bank.deeplink.android
            };scheme=https;end`;
      }

      return baseUrl;
    },

    selectBank(bank) {
      const deeplink = this.generateDeeplink(bank);

      const link = document.createElement('a');
      link.href = deeplink;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.closeBankModal();
    },
  },
};
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
$--blue-color: #125cd3;
$--white-color: #ffffff;

$--label-color-1: #7f8fa4;
$--label-color-2: #334150;
$--card-label-color: #b3bdff;
$--card-value-color: $--white-color;

$--step-bg-color: #f1f3ff;
$--step-label-color-1: $--blue-color;

$--mtb-bg-color: #125cd3;
$--button-bg-color: #125cd3;
$--button-hover-bg-color: $--blue-color;
$--button-active-bg-color: #125cd3;
$--button-disabled-bg-color: #d6daf5;

.mtb-page {
  font-family: 'Roboto', sans-serif;
  &__description {
    text-align: center;
    letter-spacing: 0.5px;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: $--label-color-1;
    margin-bottom: 24px;
  }

  &__qr {
    &-title {
      @media (max-width: 480px) {
        display: none;
      }

      margin-top: 20px;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.5px;
      color: $--label-color-2;
      margin-bottom: 4px;
    }
  }

  &__alt {
    @media (min-width: 480px) {
      display: none;
    }

    padding: 4px 0;
    margin: 16px 0;
    position: relative;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: #cdd5db;

    &::before,
    &::after {
      content: '';
      position: absolute;
      height: 2px;
      background: #eaecee;
      width: calc(43% - 16px);
      top: calc(50% - 2px);
    }

    &::before {
      left: 0;
    }

    &::after {
      right: 0;
    }
  }

  &__deep-link {
    padding: 9px 16px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border-radius: 6px;
    background: $--mtb-bg-color;
    width: 100%;
    max-width: 311px;
    height: 40px;
    margin: 0 auto 24px;
    cursor: pointer;
    font-size: 18px;
    color: $--white-color;
    line-height: 20px;
    letter-spacing: 0.5px;
    border: none;
    display: none;

    @media (max-width: 480px) {
      margin: 0 auto 16px;
      display: flex;
    }

    &:hover {
      background: $--button-hover-bg-color;
    }

    &:active {
      background: $--button-active-bg-color;
    }
  }

  &__modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;

    @media (max-width: 480px) {
      position: absolute;
      display: block;
    }
  }

  &__modal {
    background: $--white-color;
    border-radius: 12px;
    width: 90%;
    max-width: 480px;
    max-height: 70vh;
    overflow: hidden;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);

    @media (max-width: 480px) {
      max-height: 100%;
      overflow: unset;
      width: 100%;
      max-width: 100%;
      border-radius: unset;
      box-shadow: none;
    }
  }

  &__modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #eaecee;
    background: $--white-color;

    @media (max-width: 480px) {
      position: sticky;
      top: 0;
      z-index: 10;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 500;
      color: $--label-color-2;
    }
  }

  &__modal-close {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: $--label-color-1;
    padding: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: $--label-color-2;
    }
  }

  &__modal-content {
    padding: 0;
    flex: 1;
    overflow: hidden;

    @media (max-width: 480px) {
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }
  }

  &__banks-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-height: 60vh;
    overflow-y: auto;

    @media (max-width: 480px) {
      max-height: none;
      height: auto;
      overflow-y: visible;
      padding-bottom: 20px;
    }
  }

  &__bank-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px 20px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    gap: 10px;

    &:hover {
      background-color: #f8f9fb;
    }
  }

  &__bank-logo {
    width: 80px;
    height: 80px;
    border-radius: 16px;
    object-fit: contain;
    background: $--white-color;
  }

  &__bank-name {
    font-size: 16px;
    font-weight: 400;
    color: $--label-color-2;
  }

  &__info {
    margin-bottom: 20px;

    &-title {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.5px;
      color: $--label-color-1;
      margin-bottom: 4px;
    }

    &-amount {
      margin-right: 8px;
    }

    &-container {
      display: flex;
      justify-content: center;
      align-items: center;
      letter-spacing: 1px;
      font-weight: 500;
      font-size: 24px;
      line-height: 32px;
      color: $--label-color-2;
    }
  }
}
</style>
